import cardsApi from 'api/embeddable_cards'
import * as types from 'constants/action_types'
import {
  selectCards,
  selectContextId,
  selectContextCacheKeyBuilder,
  selectContextBuilder,
} from 'selectors/embeddable_cards'
import GrooveCards from 'api/GrooveCards'

const fetchEmbeddableCardContentFetching = (
  key,
  contextId,
  contextCacheKey
) => ({
  type: types.FETCH_EMBEDDABLE_CARD_CONTENT_FETCHING,
  data: {
    key,
    contextId,
    contextCacheKey,
  },
})

const fetchEmbeddableCardContentReceived = (
  key,
  contextId,
  contextCacheKey,
  content,
  stillLoading
) => ({
  type: types.FETCH_EMBEDDABLE_CARD_CONTENT_RECEIVED,
  data: {
    key,
    contextId,
    contextCacheKey,
    content,
    stillLoading,
  },
})

const fetchEmbeddableCardContentErrored = (
  key,
  contextId,
  contextCacheKey,
  proxyError,
  errorMessage
) => ({
  type: types.FETCH_EMBEDDABLE_CARD_CONTENT_ERRORED,
  error: true,
  data: {
    key,
    contextId,
    contextCacheKey,
    errorMessage,
    proxyError,
  },
})

export function triggerEmbeddableCardErrored(
  key,
  contextId,
  contextCacheKey,
  errorMessage
) {
  return {
    type: types.TRIGGER_EMBEDDABLE_CARD_ERRORED,
    error: true,
    data: {
      key,
      contextId,
      contextCacheKey,
      errorMessage,
    },
  }
}

export function triggerEmbeddableCardSuccess(key, contextId, contextCacheKey) {
  return {
    type: types.TRIGGER_EMBEDDABLE_CARD_SUCCESS,
    data: {
      key,
      contextId,
      contextCacheKey,
    },
  }
}

export function doFetchEmbeddableCardContents(card, location, useCache = true) {
  return (dispatch, getState) => {
    const state = getState()
    const contextCacheKey = selectContextCacheKeyBuilder(state)(location)
    const context = selectContextBuilder(state)(location)
    const contextId = selectContextId(state)
    const cacheEntry = card.content && card.content[contextCacheKey]

    if (!context) return null // If there isn't a full context yet we can't load our cards
    if (
      cacheEntry &&
      cacheEntry.content &&
      cacheEntry.cacheKey === contextCacheKey &&
      useCache
    )
      return true // If the card's content's cacheKey matches the current one we don't need to load the content
    if (
      cacheEntry &&
      cacheEntry.errored &&
      cacheEntry.cacheKey === contextCacheKey
    )
      return true // If the last fetch errored for this cache key, don't bother
    if (cacheEntry && cacheEntry.fetching) return true // We're already fetching this card
    const registeredCard = GrooveCards.cards[card.key]
    if (registeredCard && registeredCard.contextChangeCallback) return true // If the card is going to handle context changes we don't need to rerequest
    const useProxy = !card.key.match(/^groove-/)

    dispatch(
      fetchEmbeddableCardContentFetching(card.key, contextId, contextCacheKey)
    )

    return cardsApi
      .getContent(card.endpoint, card.authorization, context, useProxy)
      .then(result => {
        const { content, stillLoading } = result
        dispatch(
          fetchEmbeddableCardContentReceived(
            card.key,
            contextId,
            contextCacheKey,
            content,
            stillLoading
          )
        )
      })
      .catch(err => {
        dispatch(
          fetchEmbeddableCardContentErrored(
            card.key,
            contextId,
            contextCacheKey,
            err.name !== 'EndpointError',
            err.message
          )
        )
      })
  }
}

export function doUpdateCardLoading(key, cacheKey, isLoading) {
  return {
    type: types.UPDATE_CARD_LOADING,
    data: {
      key,
      cacheKey,
      isLoading,
    },
  }
}

export function doUpdateCardCollapsing(key, cacheKey, isCollapsed) {
  return {
    type: types.UPDATE_CARD_COLLAPSED,
    data: {
      key,
      cacheKey,
      isCollapsed,
    },
  }
}

export function doUpdateCardHidden(key, isHidden) {
  return {
    type: types.UPDATE_CARD_HIDDEN,
    data: {
      key,
      isHidden,
    },
  }
}

export function doUpdateCardTabTitle(key, title) {
  return {
    type: types.UPDATE_CARD_TAB_TITLE,
    data: { key, title },
  }
}

export function doActivateCardTab(key) {
  return {
    type: types.ACTIVATE_CARD_TAB,
    data: { key },
  }
}

export function doContextChangeRegistered(key) {
  return {
    type: types.CARD_ON_CONTEXT_CHANGE_REGISTERED,
    data: { key },
  }
}
